import React, { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ArtavaAttribution from "./artava/ArtavaAttribution";
import styles from "./PageHelp.module.css";

const getTemperatureAnalyzerHelp = (isItalian) => ({
  title: isItalian ? "Analizzatore Temperatura" : "Temperature Analyzer",
  purpose: isItalian
    ? "Questa pagina mostra come i FAIL burn-in cambiano al cambiare della temperatura."
    : "This page shows how burn-in failures change as temperature changes.",
  sections: isItalian
    ? [
        { heading: "Che cosa mostra questa pagina?", body: "Questa pagina confronta volume, FAIL e step critici per temperatura o fascia termica." },
        { heading: "Come leggere i dati", body: "Le card mostrano il riepilogo del perimetro selezionato. I grafici mostrano quali temperature hanno più FAIL, quali sezioni soffrono di più e quali step cambiano di più tra freddo, ambiente e caldo." },
        { heading: "Che cosa significa la correlazione?", body: "Correlazione positiva significa che i FAIL crescono quando la temperatura sale. Correlazione negativa significa che i FAIL diminuiscono quando la temperatura sale. Più il valore è vicino a 1 o -1, più la relazione è forte." },
      ]
    : [
        { heading: "What this page shows", body: "This page compares burn-in volume, failures, and critical steps by temperature or temperature bucket." },
        { heading: "How to read the data", body: "The cards summarize the selected scope. The charts show which temperatures have more failures, which sections suffer most, and which steps change the most between cold, ambient, and hot conditions." },
        { heading: "What correlation means", body: "A positive correlation means failures rise as temperature rises. A negative correlation means failures fall as temperature rises. Values closer to 1 or -1 mean a stronger relationship." },
      ],
});

const burnInChartHelp = {
  "/burn-in-pie-chart": {
    title: "Burn-In Pie Chart",
    purpose: "This page shows how the selected burn-in data is split by result status.",
    sections: [
      { heading: "What this page shows", body: "This page shows the overall result mix of the selected burn-in rows, such as PASS, FAIL, and any other stored status labels." },
      { heading: "What the cards mean", body: "The cards summarize the selected scope. Status buckets are the number of different status labels found in the current data. If you see PASS and FAIL only, the bucket count is 2. The other cards show totals such as rows, failed rows, numeric rows, or step coverage depending on the current page layout." },
      { heading: "How to read the chart", body: "Each slice shows how much of the current burn-in data belongs to one status. A large FAIL slice means many rows in the current scope are failing. Use this page as the first quick check before opening Pareto or root-cause pages." },
    ],
  },
  "/burn-in-pareto-chart": {
    title: "Burn-In Pareto Chart",
    purpose: "This page shows which burn-in steps create the most failed rows.",
    sections: [
      { heading: "What this page shows", body: "This page ranks burn-in steps from the highest fail count to the lowest fail count." },
      { heading: "How to read the chart", body: "The bars show fail count for each step. The line shows cumulative percent, which means how much of the total failure load is already explained as you move from left to right. The 80 percent marker helps you see the small group of steps causing most of the failures." },
      { heading: "What the extra details mean", body: "The table under the chart can also show related context such as the parent performance-test section, temperature phase, or measured-value notes for the same failing step. Use this page to decide which steps deserve investigation first." },
    ],
  },
  "/burn-in-status": {
    title: "Burn-In Status Overview",
    purpose: "This page shows the overall status breakdown of the selected burn-in data.",
    sections: [
      { heading: "What this page shows", body: "This page shows how many burn-in rows are PASS, FAIL, or another stored status in the current filter scope." },
      { heading: "What the summary means", body: "The summary numbers give a quick overview of total rows, failed rows, and other scope totals. They help you understand whether the current workbook or filter looks broadly healthy or not." },
      { heading: "How to use it", body: "Use this page as an overview. If FAIL looks high, open Pareto or Step Fail Rate next. If the status mix looks acceptable but you still suspect drift, open a numeric page such as Control Chart or Near-Limit Risk." },
    ],
  },
  "/burn-in-failure-pareto": {
    title: "Burn-In Failure Pareto",
    purpose: "This page shows which steps are carrying most of the burn-in failures.",
    sections: [
      { heading: "What this page shows", body: "This page is a fail-count ranking of burn-in steps." },
      { heading: "How to read it", body: "Higher bars mean more failed rows for that step. The cumulative line shows how quickly the total failure load builds up across the ranked steps." },
      { heading: "Why it matters", body: "Use this page when you want to remove the biggest amount of failure first. Then compare the top steps with Step Fail Rate or Sequence Hotspots to see whether they are high-volume problems or truly unstable steps." },
    ],
  },
  "/burn-in-step-fail-rate": {
    title: "Burn-In Step Fail Rate",
    purpose: "This page shows which steps fail most often by percentage.",
    sections: [
      { heading: "What this page shows", body: "This page ranks steps by fail rate, not by total fail count." },
      { heading: "How to read it", body: "Fail rate means failed rows divided by total rows for the same step. A step can appear high here even if it does not have the biggest fail count, because it fails very often when it appears." },
      { heading: "Why it matters", body: "Use this page to catch unstable steps that may be hidden in a normal Pareto. Compare it with Step Volume so you do not confuse a tiny noisy step with a large real process issue." },
    ],
  },
  "/burn-in-step-volume": {
    title: "Burn-In Step Volume",
    purpose: "This page shows how often each burn-in step appears.",
    sections: [
      { heading: "What this page shows", body: "This page compares total row volume and fail volume for each burn-in step." },
      { heading: "What the numbers mean", body: "A step with high total volume appears in many rows. A step with high fail volume is contributing many failed rows. Looking at both together helps you understand whether a step is important because it is common, risky, or both." },
      { heading: "How to use it", body: "Use this page together with Step Fail Rate and Pareto. A high-volume step with even a moderate fail rate can still create a large production burden." },
    ],
  },
  "/burn-in-control-chart": {
    title: "Burn-In Control Chart",
    purpose: "This page shows whether a selected numeric burn-in measurement is stable over time.",
    sections: [
      { heading: "What this page shows", body: "This page shows one or more numeric burn-in series with average line, control limits, and supporting detail cards." },
      { heading: "What the lines and cards mean", body: "The center line is the average. UCL and LCL are the control limits. The detail cards below the chart show capability and spread information such as Cp, Cpk, Pp, and Ppk when available." },
      { heading: "How to use it", body: "Use this page to spot drift, jumps, widening spread, or unstable numeric behavior before it becomes a clear fail-count problem. If a series looks risky here, compare it with Limit Compliance, Near-Limit Risk, or Value Spread." },
    ],
  },
  "/burn-in-uut-status-summary": {
    title: "UUT Status Summary",
    purpose: "This page shows burn-in results grouped by unit or serial context.",
    sections: [
      { heading: "What this page shows", body: "This page summarizes burn-in performance at unit level instead of raw row level." },
      { heading: "What the bars mean", body: "Each unit bucket shows how many PASS rows and FAIL rows belong to that unit or serial context. This helps you see whether the problem is concentrated in a few units or spread across many units." },
      { heading: "How to use it", body: "If only a few units are weak, inspect them in Burn-In Files or compare their failing steps in Pareto and Root Cause Analyzer. If many units are weak, the issue is more likely to be systemic." },
    ],
  },
  "/burn-in-root-cause-analyzer": {
    title: "Root Cause Analyzer",
    purpose: "This page shows which step most often appears first when a burn-in context starts failing.",
    sections: [
      { heading: "What this page shows", body: "This page is a first-fail ranking for burn-in contexts." },
      { heading: "What first fail means", body: "First fail means the earliest failing step seen inside one source, unit, or filtered context. A step that fails first many times is often more important than a step that fails later as a consequence." },
      { heading: "How to use it", body: "Use this page to separate likely triggers from downstream noise. Then compare the top steps with Pareto and Sequence Hotspots to confirm whether the same step is both early and frequent." },
    ],
  },
  "/burn-in-timeline": {
    title: "Burn-In Timeline",
    purpose: "This page shows how burn-in volume and failures change over time.",
    sections: [
      { heading: "What this page shows", body: "This page trends burn-in rows and failed rows by day or time bucket." },
      { heading: "How to read it", body: "Rising total volume means more burn-in activity. Rising fail volume or fail rate means the process is getting worse in the selected period." },
      { heading: "How to use it", body: "Use this page to find bad days or sudden changes. If one period jumps, compare that same period in Upload Comparison or Source Health." },
    ],
  },
  "/burn-in-hourly-activity": {
    title: "Burn-In Hourly Activity",
    purpose: "This page shows whether burn-in behavior changes by hour of day.",
    sections: [
      { heading: "What this page shows", body: "This page compares burn-in activity and failures across the hours of the day." },
      { heading: "How to read it", body: "Each hour bucket shows how much burn-in traffic and how many failures happened in that time window. This helps you spot shift effects or weak operating periods." },
      { heading: "How to use it", body: "If failures are concentrated at certain hours, compare the same scope with Source Health or Upload Comparison to see whether the issue belongs to one source or a broader operating pattern." },
    ],
  },
  "/burn-in-source-health": {
    title: "Burn-In Source Health",
    purpose: "This page compares the source files or source labels inside the burn-in data.",
    sections: [
      { heading: "What this page shows", body: "This page compares each burn-in source by size, fail count, fail rate, and coverage." },
      { heading: "What the rows mean", body: "Each row is one source label or embedded source file. It shows whether one source is much weaker than the others." },
      { heading: "How to use it", body: "If one source stands out, drill into its top steps with Pareto and check its numeric behavior with Limit Compliance or Near-Limit Risk." },
    ],
  },
  "/burn-in-upload-comparison": {
    title: "Burn-In Upload Comparison",
    purpose: "This page compares imported burn-in uploads against each other.",
    sections: [
      { heading: "What this page shows", body: "This page compares whole burn-in uploads by size, fail behavior, and structure." },
      { heading: "What the comparison means", body: "Each upload row summarizes one imported workbook. It helps you see whether one upload is clearly worse, larger, or more failure-heavy than the others." },
      { heading: "How to use it", body: "Use this page when you want to know whether the issue belongs to one workbook only or repeats across many imports. Then drill into the abnormal upload with other burn-in pages." },
    ],
  },
  "/burn-in-units": {
    title: "Burn-In Units Breakdown",
    purpose: "This page groups the data by the Units field.",
    sections: [
      { heading: "What this page shows", body: "This page shows how the selected burn-in rows are split by unit label, such as numeric units, logical values, or other measurement families." },
      { heading: "Why it matters", body: "If numeric units are a small part of the data, charts based on numeric spread or limits will naturally have less coverage. If text-like units dominate, categorical pages become more important." },
      { heading: "How to use it", body: "Use this page to understand what kind of data is inside the current scope before trusting more specialized analyses." },
    ],
  },
  "/burn-in-data-completeness": {
    title: "Burn-In Data Completeness",
    purpose: "This page checks whether the key burn-in fields are well populated.",
    sections: [
      { heading: "What this page shows", body: "This page is a coverage check for important burn-in fields such as time, units, numeric values, and limits." },
      { heading: "How to read it", body: "High coverage means the related chart logic is better supported. Low coverage means related analyses should be read more carefully." },
      { heading: "How to use it", body: "Use this page before relying heavily on time-based, numeric, or limit-based charts. Missing data here explains why some analyses may look weak or empty." },
    ],
  },
  "/burn-in-sequence-hotspots": {
    title: "Burn-In Sequence Hotspots",
    purpose: "This page shows which exact sequence positions fail the most.",
    sections: [
      { heading: "What this page shows", body: "This page ranks exact burn-in sequence slots by fail concentration." },
      { heading: "Why sequence matters", body: "A step name may appear in more than one place in the flow. This page helps you see whether one exact slot in the sequence is much weaker than the others." },
      { heading: "How to use it", body: "Use this page when you suspect the problem is tied to where the step happens in the flow, not only to the step label itself. Then compare it with Sequence Bands." },
    ],
  },
  "/burn-in-sequence-bands": {
    title: "Burn-In Sequence Bands",
    purpose: "This page compresses the sequence into wider phases.",
    sections: [
      { heading: "What this page shows", body: "This page groups burn-in sequence positions into larger bands so you can see whether early, middle, or late parts of the run are weaker." },
      { heading: "How to read it", body: "Each band combines many nearby sequence slots. This makes broad weak phases easier to see than in a very detailed exact-slot view." },
      { heading: "How to use it", body: "Use this page to find the weak region first, then open Sequence Hotspots to locate the exact sequence positions inside that weak band." },
    ],
  },
  "/burn-in-value-spread": {
    title: "Burn-In Numeric Spread",
    purpose: "This page shows which numeric burn-in steps have the widest spread.",
    sections: [
      { heading: "What this page shows", body: "This page ranks numeric step and unit pairs by how wide their values spread from minimum to maximum." },
      { heading: "What the numbers mean", body: "Large spread means the values are moving a lot. That can point to unstable behavior, but it does not always mean the values are already out of spec." },
      { heading: "How to use it", body: "Use this page to find noisy numeric steps, then compare them with Limit Compliance and Near-Limit Risk to understand whether the spread is actually dangerous." },
    ],
  },
  "/burn-in-limit-compliance": {
    title: "Burn-In Limit Compliance",
    purpose: "This page checks numeric burn-in values directly against their limits.",
    sections: [
      { heading: "What this page shows", body: "This page shows which steps produce the most out-of-spec numeric rows." },
      { heading: "How to read it", body: "Comparable rows are rows where Smarti can read both the value and the limits. Out-of-spec rows are the values that fall below the low limit or above the high limit." },
      { heading: "How to use it", body: "Use this page to confirm whether failures are truly connected to numeric limit violations. Then compare those same steps with Value Spread or Control Chart." },
    ],
  },
  "/burn-in-near-limit-risk": {
    title: "Burn-In Near-Limit Risk",
    purpose: "This page finds values that still pass but are too close to the limit.",
    sections: [
      { heading: "What this page shows", body: "This page ranks steps whose passing values have the least room before hitting a specification limit." },
      { heading: "What the margins mean", body: "A small margin means the values are still passing, but they are very close to the allowed boundary. That is often an early warning before true out-of-spec failures appear." },
      { heading: "How to use it", body: "Use this page to catch drift early. If a step looks risky here, track it over time in Timeline or compare uploads to see whether the margin is getting worse." },
    ],
  },
  "/burn-in-categorical-values": {
    title: "Burn-In Categorical Values",
    purpose: "This page summarizes non-numeric burn-in values such as flags, codes, and text states.",
    sections: [
      { heading: "What this page shows", body: "This page shows the most common non-numeric values found in the selected burn-in data." },
      { heading: "What the rows mean", body: "Each row is one repeated text value, code, or state. The page shows how often it appears and how much failure is associated with it." },
      { heading: "How to use it", body: "Use this page when the important signal is not numeric. It helps you spot repeated logical states, codes, or strings that appear unusually often with failures." },
    ],
  },
};

const productionChartHelp = {
  "/production-dashboard": {
    title: "Dashboard",
    purpose: "This page gives the main production overview for uploaded machine-test data.",
    sections: [
      { heading: "What this page shows", body: "This page shows the main production summary for the selected period, including overall health, failure concentration, and shortcuts to deeper analysis." },
      { heading: "How to read it", body: "The cards and widgets are quick signals. If overall FAIL is high, start from the failure widgets. If one trend or metric looks abnormal, open the matching detailed page from the dashboard." },
      { heading: "How to use it", body: "Use this as the first production page. It helps you decide whether the problem is broad, limited to a few steps, or related to one deeper issue such as drift, retries, or repeated alarms." },
    ],
  },
  "/upload": {
    title: "Upload New Files",
    purpose: "This page imports new production test files into the main analysis database.",
    sections: [
      { heading: "What this page shows", body: "This page lets you import new production test files into Smarti." },
      { heading: "How it works", body: "Each uploaded file is parsed and saved into the production database. Session-level information goes into the main metadata table and step-level results go into the test-step table." },
      { heading: "How to use it", body: "Use this page whenever you want to refresh the production data. After upload, check View All Files or the Dashboard to confirm the new data is present." },
    ],
  },
  "/file-table": {
    title: "View All Files",
    purpose: "This page reviews the imported production records and their stored details.",
    sections: [
      { heading: "What this page shows", body: "This page shows the uploaded production files, stored sessions, and raw details behind the production charts." },
      { heading: "How to read it", body: "Use the filters to narrow to the sessions or files you want. Open details when you need to inspect the real stored data behind a chart." },
      { heading: "How to use it", body: "Use this page to verify imports, validate filter scope, and check raw session content before drawing conclusions from a chart." },
    ],
  },
  "/pie-chart": {
    title: "Pie Chart Analysis",
    purpose: "This page compares the overall PASS and FAIL balance.",
    sections: [
      { heading: "What this page shows", body: "This page shows the overall production result mix, mainly PASS versus FAIL, for the selected scope." },
      { heading: "How to read it", body: "Each slice shows how much of the selected production data belongs to one result status. A larger FAIL slice means the current scope is less healthy." },
      { heading: "How to use it", body: "Use this as a quick first check. If FAIL looks high, open Pareto or UUT Status Summary next." },
    ],
  },
  "/pareto-chart": {
    title: "Pareto Chart Analysis",
    purpose: "This page ranks the steps contributing the most failure load.",
    sections: [
      { heading: "What this page shows", body: "This page shows which production steps create the highest fail count." },
      { heading: "How to read it", body: "The bars show fail count for each step. The cumulative line shows how much of the total failure load is already explained as you move from left to right." },
      { heading: "How to use it", body: "Use this page to choose which failing steps deserve attention first. Then compare those top steps with root-cause or first-fail pages." },
    ],
  },
  "/control-chart": {
    title: "Control Chart Analysis",
    purpose: "This page checks whether a selected numeric production step behaves like a stable and capable process.",
    sections: [
      { heading: "What this page shows", body: "This page shows whether a selected numeric production measurement is stable over time." },
      { heading: "How to read it", body: "The control-chart views show individual values, moving range, and distribution behavior. The center line is the average, while the control limits show the normal operating band." },
      { heading: "How to use it", body: "Use this page to detect drift, jumps, unstable spread, or values getting too close to limits. If it looks risky, compare it with Normal Distribution or Limit Margin Risk." },
    ],
  },
  "/new-control-chart": {
    title: "Detailed Control Chart",
    purpose: "This page expands a selected measurement series into a larger diagnostic view.",
    sections: [
      { heading: "What this page shows", body: "This page shows the same selected control-chart series in a larger and more detailed view." },
      { heading: "How to read it", body: "It follows the same logic as the main control-chart page, but gives you more space to inspect the series, limits, spread, and supporting detail." },
      { heading: "How to use it", body: "Use this page when you want to focus on one measurement more carefully than the main control page allows." },
    ],
  },
  "/uut-status-summary": {
    title: "UUT Status Summary",
    purpose: "This page compares status counts by serial-number grouping.",
    sections: [
      { heading: "What this page shows", body: "This page shows production results grouped at unit or serial-number level instead of only overall totals." },
      { heading: "How to read it", body: "Each bar group shows how many PASS, FAIL, STOP, or other statuses belong to the same serial-number context." },
      { heading: "How to use it", body: "Use this page to see whether the problem is concentrated in a few units or spread across many units." },
    ],
  },
  "/root-cause-prediction": {
    title: "Root Cause Analyzer",
    purpose: "This page estimates which other steps are most strongly linked to a selected failed step.",
    sections: [
      { heading: "What this page shows", body: "This page shows which other production steps or contexts most often appear together with a selected failing step." },
      { heading: "How to read it", body: "Higher-ranked suspects are more strongly associated with the chosen failure in the current dataset." },
      { heading: "How to use it", body: "Use Pareto first to choose an important failing step, then use this page to investigate what may be connected to it." },
    ],
  },
  "/root-cause-alarms": {
    title: "Root Cause Alarms",
    purpose: "This page surfaces repeated failure relationships that deserve immediate attention.",
    sections: [
      { heading: "What this page shows", body: "This page shows repeated production failure patterns that look important enough to review quickly." },
      { heading: "How to read it", body: "Each alarm is a repeated or suspicious failure relationship. Higher-priority alarms deserve earlier investigation." },
      { heading: "How to use it", body: "Use this page when there are many problems and you want a short list of patterns worth checking first." },
    ],
  },
  "/step-frequency": {
    title: "Step Frequency Analyzer",
    purpose: "This page shows how often each production step appears and how often it fails.",
    sections: [
      { heading: "What this page shows", body: "This page shows how often each production step appears and how much fail burden is attached to it." },
      { heading: "How to read it", body: "A step with high frequency appears often in the flow. A step with both high frequency and high fail count deserves more attention." },
      { heading: "How to use it", body: "Use this page to understand traffic and exposure, especially when you suspect repeated retries, loops, or overused steps." },
    ],
  },
  "/normal-distribution": {
    title: "Process Capability Report",
    purpose: "This page checks whether a selected numeric measurement fits comfortably within its limits.",
    sections: [
      { heading: "What this page shows", body: "This page shows the distribution of one numeric production measurement compared with its allowed limits." },
      { heading: "How to read it", body: "The distribution shape shows spread and centering. If the values are too wide or too close to the limits, the process is riskier." },
      { heading: "How to use it", body: "Use this page to check whether a numeric process has enough room inside specification before it becomes a stronger fail-count problem." },
    ],
  },
  "/ml/runs": {
    title: "ML Runs",
    purpose: "This page predicts failing sessions and explains likely failure sources using the production ML workflow.",
    sections: [
      { heading: "What this page shows", body: "This page shows the production machine-learning runs, predictions, related evidence, and run logs." },
      { heading: "How to read it", body: "Predictions estimate which sessions are likely PASS or FAIL. The related tabs show patterns, suspects, and technical logs from the run." },
      { heading: "How to use it", body: "Use this page to add predictive insight on top of the normal charts, then validate important ML findings with Pareto, root-cause, and numeric pages." },
    ],
  },
  "/first-fail-fingerprint": {
    title: "First-Fail Fingerprint",
    purpose: "This page isolates which step fails first before later failures bury the trigger.",
    sections: [
      { heading: "What this page shows", body: "This page shows which production step most often fails first inside failed sessions." },
      { heading: "How to read it", body: "A step that appears often as the first fail is a stronger trigger candidate than a step that mostly fails later." },
      { heading: "How to use it", body: "Use this page to separate likely triggers from downstream noise. Then compare the top steps with Pareto." },
    ],
  },
  "/limit-margin-risk": {
    title: "Limit Margin Risk",
    purpose: "This page catches passing measurements that are drifting dangerously close to their limits.",
    sections: [
      { heading: "What this page shows", body: "This page shows numeric production steps whose passing values are too close to their limits." },
      { heading: "How to read it", body: "A small margin means the step is still passing, but it has little room before going out of specification." },
      { heading: "How to use it", body: "Use this page to catch drift early, before the issue becomes visible in overall fail counts." },
    ],
  },
  "/production-hotspots": {
    title: "Production Hotspots",
    purpose: "This page compares production context groups against the baseline fail rate.",
    sections: [
      { heading: "What this page shows", body: "This page shows which stations, versions, operators, fixtures, or other context groups are performing worse than baseline." },
      { heading: "How to read it", body: "A hotspot is a group with a higher fail rate than the overall production scope. Larger sample size makes the hotspot more trustworthy." },
      { heading: "How to use it", body: "Use this page to see whether the problem belongs to a specific context instead of the whole process." },
    ],
  },
  "/cascade-failure-spread": {
    title: "Cascade Failure Spread",
    purpose: "This page shows which first failures tend to trigger long downstream failure chains.",
    sections: [
      { heading: "What this page shows", body: "This page shows which first production failures tend to create many more failures later in the same session." },
      { heading: "How to read it", body: "A strong cascade step is one that is often followed by many downstream failures." },
      { heading: "How to use it", body: "Use this page to find trigger steps that create large failure chains and extra noise elsewhere in the run." },
    ],
  },
  "/retry-pressure": {
    title: "Retry Pressure",
    purpose: "This page shows which sections consume retries, time, and line effort.",
    sections: [
      { heading: "What this page shows", body: "This page shows which production sections are consuming retries, extra time, and operator effort." },
      { heading: "How to read it", body: "High retry pressure means a section needs repeated attempts more often than it should." },
      { heading: "How to use it", body: "Use this page to catch fragile behavior early, even before it becomes one of the highest fail-count problems." },
    ],
  },
  "/late-failure-cost": {
    title: "Late-Stage Failure Cost",
    purpose: "This page ranks failures by how late they happen in the run and how much cycle time they waste.",
    sections: [
      { heading: "What this page shows", body: "This page shows which failures happen late in the run, after more cycle time has already been spent." },
      { heading: "How to read it", body: "A step that fails late is more expensive because the unit has already consumed more test time before failing." },
      { heading: "How to use it", body: "Use this page to balance fail count with real time cost. Some moderate-count failures still deserve priority because they happen very late." },
    ],
  },
};

const smartiLabHelp = {
  "/smarti-lab": {
    title: "Smarti Lab",
    purpose: "Questa è l’area sperimentale di Smarti dedicata ad analisi più profonde, senza toccare le dashboard principali.",
    sections: [
      { heading: "Che cos'è questa pagina?", body: "È la panoramica del Lab: raccoglie copertura dati, rischi principali, feed operativo e scorciatoie verso le analisi sperimentali più utili." },
      { heading: "Quale logica backend usa?", body: "Il backend legge dati reali da global_metadata, step_data, burn_in_uploads e burn_in_measurements. Le query sono in sola lettura e applicano sempre lo scope utente o admin prima di costruire i riepiloghi." },
      { heading: "Perché è utile?", body: "Serve per capire se il dataset attuale è abbastanza ricco per analisi avanzate e per scegliere rapidamente dove iniziare: unità, stazioni, regressioni, near-limit, burn-in o collegamenti cross-dataset." },
      { heading: "Che cosa sta mostrando?", body: "Mostra quantità di test e misure disponibili, fail rate, stazioni coperte, rischi principali, unità/stazioni deboli, sorgenti burn-in critiche e hotspot near-limit." },
    ],
  },
  "/smarti-lab/priority-queue": {
    title: "Coda priorità",
    purpose: "Questa pagina ordina i segnali più urgenti che Smarti Lab trova nel dataset corrente.",
    sections: [
      { heading: "Che cos'è questa pagina?", body: "È una vista di triage: mette nello stesso elenco unità rischiose, stazioni deboli, regressioni, drift near-limit, degrado burn-in e collegamenti cross-dataset sospetti." },
      { heading: "Quale logica backend usa?", body: "Il backend combina più analisi del Lab e assegna priorità con regole trasparenti basate su severità, frequenza, recenza e qualità dell’evidenza. Non usa ML: sono regole spiegabili." },
      { heading: "Perché è utile?", body: "Aiuta a non disperdere l’attenzione quando ci sono molti segnali contemporaneamente. Invece di aprire dieci pagine, qui vedi cosa conviene indagare prima." },
      { heading: "Che cosa sta mostrando?", body: "Ogni riga rappresenta un rischio o un’anomalia già sintetizzata: categoria, severità, priorità numerica, riassunto e principali evidenze che hanno generato il ranking." },
    ],
  },
  "/smarti-lab/data-quality": {
    title: "Qualità dati",
    purpose: "Questa pagina misura quanto il dataset è affidabile per produrre insight utili nel Lab.",
    sections: [
      { heading: "Che cos'è questa pagina?", body: "È una diagnostica di copertura e qualità: controlla presenza di date, limiti, valori numerici, stazioni, versioni ATE, Testspec e qualità del collegamento con il burn-in." },
      { heading: "Quale logica backend usa?", body: "Il backend calcola coperture percentuali sui campi chiave leggendo direttamente le tabelle di produzione e burn-in. Quando un campo manca o è poco popolato, il Lab lo segnala apertamente." },
      { heading: "Perché è utile?", body: "Serve per capire quanto fidarsi delle analisi. Se mancano limiti, l’analisi near-limit sarà debole; se mancano timestamp burn-in, i trend cronologici saranno meno solidi." },
      { heading: "Che cosa sta mostrando?", body: "Mostra copertura produzione, copertura burn-in, qualità dei collegamenti cross-dataset e un elenco di problemi che possono indebolire gli insight." },
    ],
  },
  "/smarti-lab/units": {
    title: "Storico unità",
    purpose: "Questa pagina ricostruisce il comportamento delle unità a livello seriale.",
    sections: [
      { heading: "Che cos'è questa pagina?", body: "È una vista per seriale: ogni riga riassume quante volte un’unità è stata testata, quanti fail ha accumulato, in quali stazioni è comparsa e quanto è rischiosa." },
      { heading: "Quale logica backend usa?", body: "Il backend raggruppa global_metadata per serialNumber, arricchisce con step_data per contare fail e near-limit, poi calcola un rischio basato su fail rate, ripetizione degli step critici e recenza dei problemi." },
      { heading: "Perché è utile?", body: "Aiuta a capire se il problema è distribuito oppure concentrato su poche unità, e permette di aprire in un click il dettaglio di un seriale sospetto." },
      { heading: "Che cosa sta mostrando?", body: "Mostra totale test, ultimo stato, primo e ultimo test, stazioni toccate, versioni ATE, Testspec, conteggio step in fail, near-limit e un’etichetta di rischio." },
    ],
  },
  "/smarti-lab/stations": {
    title: "Salute stazioni",
    purpose: "Questa pagina confronta le stazioni di test per capire quali stanno degradando la qualità.",
    sections: [
      { heading: "Che cos'è questa pagina?", body: "È una classifica di salute delle stazioni: mostra quali stazioni hanno più fail, quali colpiscono più seriali e quali concentrano gli step peggiori." },
      { heading: "Quale logica backend usa?", body: "Il backend raggruppa i test per stationName, calcola pass/fail, durata media, seriali unici, step critici e assegna uno score di salute con etichetta healthy, watch, warning o critical." },
      { heading: "Perché è utile?", body: "Serve per distinguere problemi di prodotto da problemi di contesto produttivo come tooling, fixture, setup macchina o versioni software associate a una stazione." },
      { heading: "Che cosa sta mostrando?", body: "Ogni riga mostra volume di test, fail rate, seriali unici, step più critico, ultimo test osservato e motivazione della valutazione di salute." },
    ],
  },
  "/smarti-lab/regressions": {
    title: "Rilevatore regressioni",
    purpose: "Questa pagina cerca peggioramenti misurabili collegati a versioni ATE o Testspec.",
    sections: [
      { heading: "Che cos'è questa pagina?", body: "È una vista di regressione trasparente: confronta gruppi ATE o Testspec per vedere se il fail rate sta salendo e se stanno comparendo nuovi step critici." },
      { heading: "Quale logica backend usa?", body: "Il backend raggruppa i test per AteSwVersion o Testspec, calcola fail rate e lo confronta con il gruppo precedente o con il periodo precedente quando la sequenza di versioni non è chiara." },
      { heading: "Perché è utile?", body: "Serve a intercettare rilasci software o revisioni di test che peggiorano la qualità, senza aspettare che il problema emerga solo dal Pareto generale." },
      { heading: "Che cosa sta mostrando?", body: "Mostra volume test, fail rate corrente, gruppo di confronto, delta fail rate, nuovi step critici e severità del candidato di regressione." },
    ],
  },
  "/smarti-lab/near-limit-drift": {
    title: "Deriva near-limit",
    purpose: "Questa pagina individua step che stanno lavorando troppo vicino ai limiti di specifica.",
    sections: [
      { heading: "Che cos'è questa pagina?", body: "È una vista di drift numerico: cerca misure ancora formalmente valide ma troppo vicine al limite basso o alto, quindi potenzialmente precursori di fail futuri." },
      { heading: "Quale logica backend usa?", body: "Il backend legge step_data con valori numerici e limiti validi, calcola la distanza percentuale dal limite più vicino, raggruppa per step/stazione/unità e confronta periodo recente contro periodo precedente." },
      { heading: "Perché è utile?", body: "Aiuta a scoprire derive prima che esplodano in fail count. È molto utile quando la dashboard principale sembra ancora sana ma alcuni processi si stanno stringendo verso il margine." },
      { heading: "Che cosa sta mostrando?", body: "Mostra near 5%, near 10%, tasso di vicinanza al limite, media/min/max, limiti, direzione della deriva e livello di rischio per ciascun gruppo." },
    ],
  },
  "/smarti-lab/repeat-offenders": {
    title: "Step recidivi",
    purpose: "Questa pagina ordina gli step che continuano a riapparire come fonte di problema.",
    sections: [
      { heading: "Che cos'è questa pagina?", body: "È una classifica degli step più recidivi: non guarda solo quanti fail fanno, ma anche quanto si ripresentano su stazioni, seriali, versioni ATE e Testspec diversi." },
      { heading: "Quale logica backend usa?", body: "Il backend raggruppa step_data per step, calcola fail count, total count, fail rate, diffusione contestuale e near-limit associato, poi assegna un repeatScore e una classificazione." },
      { heading: "Perché è utile?", body: "Serve per separare un problema locale da un problema sistemico. Se uno step fallisce ovunque, con tanti seriali e tanti contesti, merita priorità alta." },
      { heading: "Che cosa sta mostrando?", body: "Mostra conteggio fail, fail rate, stazioni colpite, seriali colpiti, versioni ATE/Testspec coinvolte, near-limit associato, score di recidiva e classificazione." },
    ],
  },
  "/smarti-lab/burn-in-degradation": {
    title: "Degrado burn-in",
    purpose: "Questa pagina cerca segnali di peggioramento nei dati burn-in tra la parte iniziale e quella finale del ciclo.",
    sections: [
      { heading: "Che cos'è questa pagina?", body: "È una vista di deriva burn-in: confronta i valori iniziali e finali degli step numerici e mostra anche quali source label stanno portando più fail." },
      { heading: "Quale logica backend usa?", body: "Il backend legge burn_in_measurements, separa fase iniziale e finale in base alla cronologia disponibile, confronta medie e delta e aggrega anche insight per source_label." },
      { heading: "Perché è utile?", body: "Serve per capire se il burn-in non sta solo fallendo, ma sta peggiorando progressivamente lungo il ciclo o concentrando il rischio in specifiche sorgenti." },
      { heading: "Che cosa sta mostrando?", body: "Mostra fail rate, delta di deriva, direzione del cambiamento, source label principali, rischio del singolo step e un riepilogo per sorgente con primo fail e step coinvolti." },
    ],
  },
  "/smarti-lab/cross-dataset-links": {
    title: "Collegamenti cross-dataset",
    purpose: "Questa pagina propone collegamenti sperimentali tra seriali di produzione e dati burn-in.",
    sections: [
      { heading: "Che cos'è questa pagina?", body: "È una pagina di matching tra due mondi separati: prova a collegare i seriali visti in produzione con source label, filename o sequenze temporali presenti nei dati burn-in." },
      { heading: "Quale logica backend usa?", body: "Il backend usa regole semplici e leggibili: match esatto normalizzato, token del seriale, regola di famiglia numerica, filename coerente e regole cronologiche sulle code numeriche. I link confermati manualmente diventano persistenti." },
      { heading: "Perché è utile?", body: "Serve a costruire una traccia comune tra produzione e burn-in, così si può capire se un’unità con segnali sospetti in produzione mostra anche degrado o fail nel burn-in." },
      { heading: "Che cosa sta mostrando?", body: "Mostra seriale, test produzione, stato più recente, label burn-in proposta, conteggio fail burn-in, ultima data, confidenza, motivo del match e stato di conferma manuale." },
    ],
  },
  "/smarti-lab/watchlist": {
    title: "Watchlist",
    purpose: "Questa pagina permette di marcare entità da monitorare con continuità dentro Smarti Lab.",
    sections: [
      { heading: "Che cos'è questa pagina?", body: "È una lista di osservazione sperimentale: puoi segnare seriali, stazioni, step, versioni ATE o Testspec da tenere sotto controllo." },
      { heading: "Quale logica backend usa?", body: "Il backend salva gli elementi in una tabella isolata del Lab, separata dalle dashboard principali. Non cambia i dati analitici: memorizza solo il monitoraggio definito dall’utente." },
      { heading: "Perché è utile?", body: "Serve per non perdere di vista elementi che oggi non sono ancora un caso aperto ma meritano osservazione continua." },
      { heading: "Che cosa sta mostrando?", body: "Mostra tipo entità, chiave, etichetta, severità, stato attivo/disattivo e azioni per aggiornare o eliminare il monitoraggio." },
    ],
  },
  "/smarti-lab/saved-views": {
    title: "Viste salvate",
    purpose: "Questa pagina salva percorsi e preset ricorrenti del Lab per riaprire più velocemente le analisi utili.",
    sections: [
      { heading: "Che cos'è questa pagina?", body: "È un archivio leggero di viste personalizzate: nome, percorso e preset che vuoi ritrovare senza ricostruire ogni volta la navigazione." },
      { heading: "Quale logica backend usa?", body: "Il backend salva le viste in una tabella isolata del Lab per utente. Non modifica nessuna dashboard esistente e non cambia i dati di produzione o burn-in." },
      { heading: "Perché è utile?", body: "Serve per creare scorciatoie personali verso i flussi di indagine che usi più spesso, soprattutto quando il Lab cresce di pagine e combinazioni." },
      { heading: "Che cosa sta mostrando?", body: "Mostra nome vista, percorso associato, ultimo aggiornamento e azioni per rimuoverla." },
    ],
  },
  "/smarti-lab/cases": {
    title: "Casi di indagine",
    purpose: "Questa pagina tiene traccia in modo leggero di indagini, ipotesi e primi passi di azione correttiva.",
    sections: [
      { heading: "Che cos'è questa pagina?", body: "È una base sperimentale di case tracking: permette di aprire un caso, collegarlo a un’entità e aggiornarne stato e priorità." },
      { heading: "Quale logica backend usa?", body: "Il backend salva i casi e i relativi eventi in tabelle isolate del Lab. Le entità collegate restano riferimenti testuali: il caso non altera né produzione né burn-in." },
      { heading: "Perché è utile?", body: "Serve per trasformare un insight del Lab in un oggetto di lavoro, così non resta solo un’anomalia vista una volta ma diventa un’indagine seguibile." },
      { heading: "Che cosa sta mostrando?", body: "Mostra titolo del caso, stato, priorità, chiave collegata e ultimo aggiornamento, oltre al form per creare rapidamente nuovi casi." },
    ],
  },
};

const smartiLabDynamicHelp = [
  {
    match: (path) => path.startsWith("/smarti-lab/units/"),
    title: "Dettaglio unità",
    purpose: "Questa pagina ricostruisce la storia completa di un singolo seriale.",
    sections: [
      { heading: "Che cos'è questa pagina?", body: "È il dossier di una singola unità: timeline dei test, step in fail, misure near-limit, stazioni attraversate, versioni ATE e possibili collegamenti burn-in." },
      { heading: "Quale logica backend usa?", body: "Il backend parte dal serialNumber, recupera i test da global_metadata, li arricchisce con step_data per fail e near-limit, poi prova a trovare candidati burn-in con le regole di matching del Lab." },
      { heading: "Perché è utile?", body: "Serve quando vuoi capire se il rischio di un’unità è episodico o strutturale, e se la stessa unità riappare con segnali coerenti anche nel burn-in." },
      { heading: "Che cosa sta mostrando?", body: "Mostra riepilogo del seriale, cronologia test, step in fail, misure vicine ai limiti, storico stazioni/versioni e candidati burn-in con confidenza e motivo del match." },
    ],
  },
  {
    match: (path) => path.startsWith("/smarti-lab/stations/"),
    title: "Dettaglio stazione",
    purpose: "Questa pagina approfondisce il comportamento di una singola stazione di test.",
    sections: [
      { heading: "Che cos'è questa pagina?", body: "È il drilldown di una stazione: trend giornaliero, top failing steps, seriali ricorrenti, versioni ATE/Testspec e file recenti associati." },
      { heading: "Quale logica backend usa?", body: "Il backend filtra global_metadata per stationName, aggrega step_data per trovare gli step peggiori, costruisce il trend nel tempo e raccoglie i contesti che rendono la stazione fragile." },
      { heading: "Perché è utile?", body: "Serve per capire se una stazione è davvero il problema, in quali giorni peggiora e se il rischio dipende da specifiche versioni, seriali o file importati." },
      { heading: "Che cosa sta mostrando?", body: "Mostra trend giornaliero, step più critici, seriali ricorrenti, distribuzione ATE e Testspec, guida all’indagine e file recenti passati da quella stazione." },
    ],
  },
];

const helpByRoute = [
  {
    match: (path) => path === "/" || path === "/login",
    title: "Login",
    purpose: "This page lets an existing user enter the system.",
    data: [
      "Username and password are sent to the server login endpoint.",
      "The server checks the users table in the SQLite database.",
      "After a successful login, the app opens the dashboard.",
    ],
  },
  {
    match: (path) => path === "/register",
    title: "Register",
    purpose: "This page creates a new user account.",
    data: [
      "The username, password, role, and secret key are sent to the server.",
      "The server checks the secret key, hashes the password, and saves the user.",
      "The user is stored in the SQLite users table.",
    ],
  },
  {
    match: (path) => path === "/loading",
    title: "Loading",
    purpose: "This page briefly prepares the app before opening the dashboard.",
    data: [
      "It does not calculate production data.",
      "It only gives the app time to move from login to the main dashboard.",
    ],
  },
  {
    match: (path) => path === "/dashboard",
    title: "Dashboard",
    purpose: "This page gives a fast overview of production health.",
    data: [
      "PASS and FAIL counts come from uploaded test result files stored in the database.",
      "The Pareto chart groups failed test steps and sorts them by fail count.",
      "Total production counts successful production records for the selected date range.",
      "The date filter sends start and end dates to the server so charts refresh from matching records.",
    ],
  },
  {
    match: (path) => path === "/upload",
    title: "Upload New Files",
    purpose: "This page imports new product test result files.",
    data: [
      "CSV files are uploaded to the backend.",
      "Global product/session information is saved in global_metadata.",
      "Each test branch or step result is saved in step_data.",
      "The charts and ML model use these stored rows after upload.",
    ],
  },
  {
    match: (path) => path === "/burn-in-dashboard",
    title: "Burn-In Dashboard",
    purpose: "This page is the main burn-in workspace dashboard built on the dedicated burn-in database.",
    data: [
      "It reads burn_in_uploads and burn_in_measurements from the separate burn_in_results.db storage.",
      "Summary tiles show upload count, measurement count, source-file count, and unit count for the current user.",
      "The shortcut cards open the featured burn-in analysis pages such as Pie Chart, Pareto, Control Chart, UUT Status Summary, and Root Cause Analyzer.",
      "The widget area reuses the same burn-in chart pages and lets users keep the most relevant burn-in analyses close by.",
    ],
  },
  {
    match: (path) => path === "/burn-in-upload",
    title: "Upload Burn-In Files",
    purpose: "This page imports the new burn-in spreadsheet format into a separate database.",
    data: [
      "Spreadsheet and CSV burn-in files are uploaded to the backend.",
      "Each import creates one row in burn_in_uploads inside burn_in_results.db.",
      "Every parsed measurement row is stored in burn_in_measurements with both normalized fields and raw values.",
      "The summary cards and preview table read from this dedicated burn-in database.",
    ],
  },
  {
    match: (path) => path === "/burn-in-files",
    title: "View Burn-In Files",
    purpose: "This page reviews the imported burn-in uploads and their parsed measurement details.",
    data: [
      "It reads upload-level records from burn_in_uploads and row-level records from burn_in_measurements.",
      "The page lets you inspect workbook metadata such as station, operator, serial number, product code, software version, and source coverage.",
      "Measurement detail views show parsed fields like source label, sequence, step name, status, measured time, numeric value, units, and raw limits.",
      "Use it to validate exactly what was imported before trusting a burn-in chart or narrowing a burn-in filter scope.",
    ],
  },
  {
    match: (path) => Boolean(burnInChartHelp[path]),
    title: "Burn-In Analytics",
    purpose: "This page explains the current burn-in chart and the burn-in data behind it.",
    data: [
      "The exact help for the current burn-in route is resolved dynamically from the burn-in help map.",
      "All burn-in analytics read from the dedicated burn_in_results.db storage rather than the production database.",
      "Most burn-in pages use burn_in_measurements, while some also summarize metadata from burn_in_uploads.",
      "Use the page-specific sections in this help modal to understand the data source, calculation logic, and best follow-up analyses for the current burn-in view.",
    ],
  },
  {
    match: (path) => path === "/file-table",
    title: "View All Files",
    purpose: "This page lists uploaded test records and files.",
    data: [
      "Rows are loaded from the server database.",
      "The table helps verify which test files have already been imported.",
      "Deleting or managing files changes what data is available for analysis.",
    ],
  },
  {
    match: (path) => path === "/pie-chart",
    title: "Pie Chart Analysis",
    purpose: "This page compares PASS and FAIL result totals.",
    data: [
      "The server counts UUT status values from global_metadata.",
      "PASS records are grouped together and FAIL records are grouped together.",
      "The chart shows the proportion of product sessions in each result group.",
    ],
  },
  {
    match: (path) => path === "/pareto-chart",
    title: "Pareto Chart Analysis",
    purpose: "This page finds the most common failing test steps.",
    data: [
      "The server reads failed rows from step_data.",
      "Failures are grouped by step number and step name.",
      "Bars show fail count. The line shows cumulative failure percentage.",
      "The highest bars are the first places to investigate.",
    ],
  },
  {
    match: (path) => path === "/control-chart",
    title: "Control Chart Analysis",
    purpose: "This page checks whether a measured process is stable over time.",
    data: [
      "Measurement values are read from test step data.",
      "The chart calculates center line and control limits from selected measurements.",
      "Points outside limits or unusual patterns can indicate process instability.",
    ],
  },
  {
    match: (path) => path === "/new-control-chart",
    title: "Detailed Control Chart",
    purpose: "This page shows expanded control chart results.",
    data: [
      "It uses selected measurement results passed from the control chart workflow.",
      "Individual and moving-range views help detect shifts, drift, and unusual variation.",
    ],
  },
  {
    match: (path) => path === "/uut-status-summary",
    title: "UUT Status Summary",
    purpose: "This page summarizes product status by selected grouping.",
    data: [
      "The server counts product sessions by UUT status.",
      "The chart shows how many records fall into each status group.",
      "It helps compare product quality across uploaded test data.",
    ],
  },
  {
    match: (path) => path === "/root-cause-prediction",
    title: "Root Cause Analyzer",
    purpose: "This page finds steps that often fail together with a selected failed step.",
    data: [
      "You choose a failing step.",
      "The server finds sessions where that step failed.",
      "It then counts other failed steps from the same sessions.",
      "Higher related counts suggest a possible connected test branch or root cause.",
    ],
  },
  {
    match: (path) => path === "/root-cause-alarms",
    title: "Root Cause Alarms",
    purpose: "This page highlights repeated failure patterns that deserve attention.",
    data: [
      "The server scans failed step data.",
      "Repeated high-frequency failures are grouped as alarm candidates.",
      "These alarms point to steps or branches that may need investigation.",
    ],
  },
  {
    match: (path) => path === "/cascade-failure-spread",
    title: "Cascade Failure Spread",
    purpose: "This page shows which first failures tend to create many more failures later in the same session.",
    data: [
      "The server starts from the first failing step in each failed session.",
      "It then counts how many additional failed steps appear after that first failure.",
      "High downstream counts suggest a step that triggers a broad failure cascade instead of an isolated issue.",
    ],
  },
  {
    match: (path) => path === "/retry-pressure",
    title: "Retry Pressure",
    purpose: "This page finds test sections that consume retries and slow production before the final result is visible.",
    data: [
      "The server reads section-level retry information from test_sections.",
      "It totals retries, retry rate, fail rate, and average section time for each section.",
      "High retry pressure often points to fragile checks, borderline timing, or unstable tooling.",
    ],
  },
  {
    match: (path) => path === "/late-failure-cost",
    title: "Late-Stage Failure Cost",
    purpose: "This page highlights fail steps that happen late in the run, after more cycle time has already been spent.",
    data: [
      "The server measures where each failing step appears inside its session.",
      "It calculates average fail position and how often that failure lands in the last part of the run.",
      "Late failures matter because they waste more test time and raise the real cost of scrap.",
    ],
  },
  {
    match: (path) => path === "/step-frequency",
    title: "Step Frequency Analyzer",
    purpose: "This page shows how often each test step appears.",
    data: [
      "The server groups step_data rows by step number and step name.",
      "Counts show how frequently each test branch appears in uploaded files.",
      "Unexpected frequency can reveal repeated retries, loops, or missing branches.",
    ],
  },
  {
    match: (path) => path === "/normal-distribution",
    title: "Process Capability Report",
    purpose: "This page checks whether measurements fit within process limits.",
    data: [
      "The page uses numeric measure values, lower limits, and upper limits from test steps.",
      "It calculates distribution and capability-style views for the selected measurement.",
      "Values near or outside limits can point to process risk.",
    ],
  },
  {
    match: (path) => path === "/predictive-analysis",
    title: "Predictive Analysis",
    purpose: "This page is for prediction-style quality analysis.",
    data: [
      "It uses historical uploaded test results.",
      "The goal is to detect product sessions or test branches that may become failures.",
      "Use the ML Runs page for the current Random Forest workflow.",
    ],
  },
  {
    match: (path) => path === "/ml-run-detail" || path.startsWith("/ml-run-detail/") || path === "/ml/runs" || path.startsWith("/ml/runs/"),
    title: "ML Runs",
    purpose: "This page predicts failing product sessions and explains likely problem sources.",
    data: [
      "The Random Forest model is trained from historical global_metadata and step_data records.",
      "Predictions mark each product test session as likely PASS or likely FAIL.",
      "The Root Cause tab ranks failing test branches by repeated failures, station risk, Testspec risk, AteSwVersion risk, retries, near-limit values, and co-failing steps.",
      "The Predictions tab shows the raw predicted label and PASS probability for each session.",
      "The Failure Patterns tab shows which test steps appear most often inside predicted-fail sessions.",
      "The Logs tab shows the train and prediction logs created by the Python scripts.",
    ],
  },
];

const fallbackHelp = {
  title: "This Page",
  purpose: "This page displays Smarti production or quality data.",
  data: [
    "Most pages read uploaded test files that were saved into the SQLite database.",
    "Product/session information comes from global_metadata.",
    "Individual test branch and step results come from step_data.",
  ],
};

function getHelpForPath(pathname, isItalian = false) {
  const smartiLabExactHelp = smartiLabHelp[pathname];
  if (smartiLabExactHelp) {
    return {
      match: () => true,
      ...smartiLabExactHelp,
    };
  }

  const smartiLabMatchedHelp = smartiLabDynamicHelp.find((item) => item.match(pathname));
  if (smartiLabMatchedHelp) {
    return smartiLabMatchedHelp;
  }

  const productionHelp = productionChartHelp[pathname];
  if (productionHelp) {
    return {
      match: () => true,
      ...productionHelp,
    };
  }

  if (pathname === "/burn-in-temperature-analyzer") {
    return {
      match: () => true,
      ...getTemperatureAnalyzerHelp(isItalian),
    };
  }

  const burnInHelp = burnInChartHelp[pathname];
  if (burnInHelp) {
    return {
      match: () => true,
      ...burnInHelp,
    };
  }

  return helpByRoute.find((item) => item.match(pathname)) || fallbackHelp;
}

export default function PageHelp() {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const help = useMemo(() => getHelpForPath(pathname, i18n.language?.startsWith('it')), [i18n.language, pathname]);
  const hideHelpButton = pathname === "/" || pathname === "/login" || pathname === "/register";

  if (hideHelpButton) return null;

  return (
    <>
      <button
        type="button"
        className={styles.helpButton}
        onClick={() => setOpen(true)}
        aria-label={t('Help for {{title}}', { title: t(help.title) })}
      >
        ?
      </button>

      {open && (
        <div className={styles.overlay} role="presentation" onMouseDown={() => setOpen(false)}>
          <section
            className={styles.dialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby="page-help-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className={styles.header}>
              <div>
                <span className={styles.eyebrow}>{t('Page Help')}</span>
                <h2 id="page-help-title">{t(help.title)}</h2>
              </div>
              <button
                type="button"
                className={styles.closeButton}
                onClick={() => setOpen(false)}
                aria-label={t('Close help')}
              >
                x
              </button>
            </div>

            <p className={styles.purpose}>{t(help.purpose)}</p>

            {Array.isArray(help.sections) ? (
              help.sections.map((section) => (
                <div key={section.heading}>
                  <h3>{t(section.heading)}</h3>
                  <p className={styles.purpose}>{t(section.body)}</p>
                </div>
              ))
            ) : (
              <>
                <h3>{t('What data am I seeing?')}</h3>
                <ul>
                  {help.data.map((item) => (
                    <li key={item}>{t(item)}</li>
                  ))}
                </ul>
              </>
            )}
            <div className={styles.footerBrand}>
              <ArtavaAttribution variant="compact" />
            </div>
          </section>
        </div>
      )}
    </>
  );
}
